import { useState, useEffect, useCallback } from "react";
import api, { presignedUpload } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  FileText, Users, Mic2, Palette, ImageIcon, Sparkles,
  Check, ChevronRight, ChevronLeft, Plus, X, Loader2, Upload
} from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { key: "details", label: "Property Details", icon: FileText },
  { key: "agent", label: "Agent", icon: Users },
  { key: "voice", label: "Voice", icon: Mic2 },
  { key: "brand", label: "Brand Kit", icon: Palette },
  { key: "photos", label: "Photos", icon: ImageIcon },
  { key: "generate", label: "Generate", icon: Sparkles },
];

export default function SetupStepper({ session, onUpdate }) {
  const [step, setStep] = useState(0);
  const [label, setLabel] = useState(session.property_label || "");
  const [desc, setDesc] = useState(session.property_details_text || "");
  const [agentId, setAgentId] = useState(session.agent_id || "");
  const [voiceId, setVoiceId] = useState(session.voice_id || "");
  const [brandId, setBrandId] = useState(session.brand_kit_id || "");
  const [photos, setPhotos] = useState(session.property_image_urls || []);
  const [agents, setAgents] = useState([]);
  const [voices, setVoices] = useState([]);
  const [brands, setBrands] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [gdriveUrl, setGdriveUrl] = useState("");
  const [importingGdrive, setImportingGdrive] = useState(false);

  useEffect(() => {
    api.get("/agents").then(r => setAgents(r.data)).catch(() => {});
    api.get("/voices").then(r => setVoices(r.data)).catch(() => {});
    api.get("/brand-kits").then(r => setBrands(r.data)).catch(() => {});
  }, []);

  const saveSession = useCallback(async (data) => {
    setSaving(true);
    try {
      const res = await api.put(`/sessions/${session.id}`, data);
      onUpdate(res.data);
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  }, [session.id, onUpdate]);

  const handleNext = async () => {
    if (step === 0) {
      if (!label.trim() || !desc.trim()) { toast.error("Please fill in property details"); return; }
      await saveSession({ property_label: label, property_details_text: desc });
    } else if (step === 1) {
      await saveSession({ agent_id: agentId || null });
    } else if (step === 2) {
      await saveSession({ voice_id: voiceId || null });
    } else if (step === 3) {
      await saveSession({ brand_kit_id: brandId || null });
    } else if (step === 4) {
      await saveSession({ property_image_urls: photos });
    }
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };

  const removePhoto = async (idx) => {
    const updated = photos.filter((_, i) => i !== idx);
    setPhotos(updated);
    try {
      await api.put(`/sessions/${session.id}`, { property_image_urls: updated });
    } catch {
      toast.error("Failed to sync photo removal");
    }
  };

  const handleFileUpload = async (fileList) => {
    const files = Array.from(fileList).filter(f => f.type.startsWith('image/'));
    if (files.length === 0) { toast.error("No image files found"); return; }
    if (photos.length + files.length > 50) { toast.error("Maximum 50 photos"); return; }
    setUploading(true);
    setUploadProgress({ current: 0, total: files.length });
    let uploaded = 0;
    for (const file of files) {
      try {
        // Upload directly to S3 via presigned URL (bypasses API Gateway 10MB limit)
        const { s3_url } = await presignedUpload(file);
        // Confirm upload with backend (runs optimization, adds to session)
        const res = await api.post(`/sessions/${session.id}/confirm-photo-upload`, { s3_url });
        setPhotos(prev => [...prev, { url: res.data.url, thumbnail_url: res.data.thumbnail_url || res.data.url }]);
        uploaded++;
        setUploadProgress({ current: uploaded, total: files.length });
      } catch {
        toast.error(`Failed: ${file.name}`);
      }
    }
    if (uploaded > 0) toast.success(`${uploaded} photo${uploaded > 1 ? 's' : ''} uploaded & optimized`);
    setUploading(false);
    setUploadProgress({ current: 0, total: 0 });
  };

  const handleGdriveImport = async () => {
    if (!gdriveUrl.trim()) return;
    setImportingGdrive(true);
    setUploadProgress({ current: 0, total: 0 });
    try {
      const res = await api.post(`/sessions/${session.id}/import-gdrive`, { folder_url: gdriveUrl });
      const importId = res.data.import_id;
      const totalFiles = res.data.total_files;
      setUploadProgress({ current: 0, total: totalFiles });
      toast.success(`Found ${totalFiles} images. Importing...`);

      // Poll for progress
      const poll = async () => {
        try {
          const statusRes = await api.get(`/gdrive-imports/${importId}`);
          const data = statusRes.data;
          setUploadProgress({ current: data.processed, total: data.total_files });

          if (data.status === 'complete') {
            // Refresh session to get updated photos
            const sessionRes = await api.get(`/sessions/${session.id}`);
            const newPhotos = sessionRes.data.property_image_urls || [];
            setPhotos(newPhotos);
            onUpdate(sessionRes.data);

            const errCount = data.errors?.length || 0;
            if (errCount > 0) {
              toast.success(`Imported ${data.uploaded_urls.length} photos (${errCount} skipped)`);
            } else {
              toast.success(`All ${data.uploaded_urls.length} photos imported & optimized`);
            }
            setGdriveUrl("");
            setImportingGdrive(false);
            setUploadProgress({ current: 0, total: 0 });
            return;
          }
          setTimeout(poll, 2000);
        } catch {
          setTimeout(poll, 3000);
        }
      };
      setTimeout(poll, 2000);
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to import from Google Drive";
      toast.error(detail);
      setImportingGdrive(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  const handleGenerate = async () => {
    if (!desc.trim()) { toast.error("Property description is required for storyboard generation"); return; }
    setGenerating(true);
    try {
      await saveSession({ property_label: label, property_details_text: desc, property_image_urls: photos, agent_id: agentId || null, voice_id: voiceId || null, brand_kit_id: brandId || null });
      const res = await api.post(`/sessions/${session.id}/generate-storyboard`);
      // Backend now returns immediately — update local session to "Generating"
      onUpdate({ ...session, status: 'Generating', generation_progress: 'Getting your storyboard ready...' });
      toast.success(res.data.message || "Your storyboard is being created!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
      setGenerating(false);
    }
  };

  const isStepComplete = (idx) => {
    if (idx === 0) return !!label.trim() && !!desc.trim();
    if (idx === 1) return !!agentId;
    if (idx === 2) return !!voiceId;
    if (idx === 3) return !!brandId;
    if (idx === 4) return photos.length > 0;
    return false;
  };

  return (
    <div className="h-full overflow-y-auto">
    <div className="p-8 max-w-4xl mx-auto" data-testid="setup-stepper">
      {/* Step indicator */}
      <div className="flex items-center gap-1 mb-10 overflow-x-auto pb-2">
        {STEPS.map((s, idx) => (
          <div key={s.key} className="flex items-center">
            <button
              onClick={() => setStep(idx)}
              data-testid={`step-${s.key}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                idx === step ? "bg-[#4D11F6] text-white" :
                isStepComplete(idx) ? "bg-emerald-50 text-emerald-700" :
                "bg-white text-[#676970] border border-[#E2E4E7]"
              }`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                idx === step ? "bg-white/20" :
                isStepComplete(idx) ? "bg-emerald-100" : "bg-[#F3F3F9]"
              }`}>
                {isStepComplete(idx) ? <Check size={12} /> : idx + 1}
              </div>
              {s.label}
            </button>
            {idx < STEPS.length - 1 && <ChevronRight size={16} className="text-[#D8D9DD] mx-1 shrink-0" />}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-xl border border-[#E2E4E7] p-8 shadow-sm animate-fade-in">
        {step === 0 && (
          <div className="space-y-6" data-testid="step-details-content">
            <div>
              <h3 className="text-xl font-semibold text-[#020B23] mb-1" style={{ fontFamily: 'Sora, sans-serif' }}>Property Details</h3>
              <p className="text-sm text-[#676970]">Enter the property information for your walkthrough video</p>
            </div>
            <div className="space-y-2">
              <Label className="text-[#1A2238] font-medium">Property Label</Label>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g., 42 Sunset Blvd, Malibu"
                data-testid="stepper-label-input" className="h-11 bg-white border-[#E2E4E7]" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#1A2238] font-medium">Property Description</Label>
              <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={6}
                placeholder="Describe the property: bedrooms, bathrooms, features, location, price, unique selling points..."
                data-testid="stepper-desc-input" className="bg-white border-[#E2E4E7] resize-none" />
              <p className="text-xs text-[#94969C]">The AI will use this description to generate the walkthrough storyboard</p>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-6" data-testid="step-agent-content">
            <div>
              <h3 className="text-xl font-semibold text-[#020B23] mb-1" style={{ fontFamily: 'Sora, sans-serif' }}>Select Agent</h3>
              <p className="text-sm text-[#676970]">Choose a virtual presenter for your video intro</p>
            </div>
            {agents.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-[#676970] mb-3">No agents available. Create one first.</p>
                <Button variant="outline" onClick={() => window.open("/agents", "_blank")} className="border-[#E2E4E7]">
                  <Plus size={14} className="mr-2" /> Create Agent
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {agents.map(agent => (
                  <button key={agent.id} onClick={() => setAgentId(agent.id)}
                    data-testid={`select-agent-${agent.id}`}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      agentId === agent.id ? "border-[#4D11F6] bg-[#4D11F6]/5" : "border-[#E2E4E7] hover:border-[#4D11F6]/30"
                    }`}>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-[#F3F3F9] overflow-hidden shrink-0">
                        {agent.image_url ? <img src={agent.image_url} alt="" className="w-full h-full object-cover" /> : <Users size={20} className="w-full h-full p-3 text-[#94969C]" />}
                      </div>
                      <div>
                        <p className="font-semibold text-[#020B23] text-sm">{agent.name}</p>
                        <Badge className="text-xs mt-1 bg-emerald-50 text-emerald-700 border-emerald-100">{agent.status}</Badge>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6" data-testid="step-voice-content">
            <div>
              <h3 className="text-xl font-semibold text-[#020B23] mb-1" style={{ fontFamily: 'Sora, sans-serif' }}>Select Voice</h3>
              <p className="text-sm text-[#676970]">Choose a narration voice for the walkthrough</p>
            </div>
            {voices.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-[#676970] mb-3">No voices available.</p>
                <Button variant="outline" onClick={() => window.open("/voices", "_blank")} className="border-[#E2E4E7]">
                  <Plus size={14} className="mr-2" /> Create Voice
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {voices.map(voice => (
                  <button key={voice.id} onClick={() => setVoiceId(voice.id)}
                    data-testid={`select-voice-${voice.id}`}
                    className={`w-full p-4 rounded-xl border-2 text-left flex items-center gap-4 transition-all ${
                      voiceId === voice.id ? "border-[#4D11F6] bg-[#4D11F6]/5" : "border-[#E2E4E7] hover:border-[#4D11F6]/30"
                    }`}>
                    <div className="w-10 h-10 rounded-lg bg-[#020B23] flex items-center justify-center shrink-0">
                      <Mic2 size={16} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-[#020B23] text-sm">{voice.name}</p>
                      <p className="text-xs text-[#94969C]">{voice.provider}</p>
                    </div>
                    <Badge className={voice.status === 'Ready' ? 'text-xs bg-emerald-50 text-emerald-700 border-emerald-100' : 'text-xs bg-amber-50 text-amber-700 border-amber-100'}>
                      {voice.status}
                    </Badge>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6" data-testid="step-brand-content">
            <div>
              <h3 className="text-xl font-semibold text-[#020B23] mb-1" style={{ fontFamily: 'Sora, sans-serif' }}>Select Brand Kit</h3>
              <p className="text-sm text-[#676970]">Choose branding for the video closing overlay</p>
            </div>
            {brands.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-[#676970] mb-3">No brand kits available.</p>
                <Button variant="outline" onClick={() => window.open("/brand-kits", "_blank")} className="border-[#E2E4E7]">
                  <Plus size={14} className="mr-2" /> Create Brand Kit
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {brands.map(kit => (
                  <button key={kit.id} onClick={() => setBrandId(kit.id)}
                    data-testid={`select-brand-${kit.id}`}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      brandId === kit.id ? "border-[#4D11F6] bg-[#4D11F6]/5" : "border-[#E2E4E7] hover:border-[#4D11F6]/30"
                    }`}>
                    <p className="font-semibold text-[#020B23] text-sm">{kit.name}</p>
                    {kit.phone && <p className="text-xs text-[#94969C] mt-1">{kit.phone}</p>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 4 && (
          <div className="space-y-6" data-testid="step-photos-content">
            <div>
              <h3 className="text-xl font-semibold text-[#020B23] mb-1" style={{ fontFamily: 'Sora, sans-serif' }}>Property Photos</h3>
              <p className="text-sm text-[#676970]">Upload photos, a folder, or import from Google Drive. Images are auto-optimized.</p>
            </div>

            {/* Upload area: files or folder */}
            <div className="grid grid-cols-2 gap-4">
              {/* Individual Files */}
              <label htmlFor="photo-upload-files"
                className="border-2 border-dashed border-[#E2E4E7] rounded-xl p-5 text-center hover:border-[#4D11F6]/30 transition-colors cursor-pointer flex flex-col items-center justify-center gap-2">
                <input type="file" id="photo-upload-files" multiple accept="image/*" className="hidden"
                  data-testid="photo-file-input"
                  disabled={uploading || importingGdrive}
                  onChange={(e) => { handleFileUpload(e.target.files); e.target.value = ''; }} />
                {uploading ? (
                  <Loader2 size={24} className="text-[#4D11F6] animate-spin" />
                ) : (
                  <Upload size={24} className="text-[#94969C]" />
                )}
                <p className="text-sm font-medium text-[#1A2238]">
                  {uploading ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...` : 'Upload Files'}
                </p>
                <p className="text-xs text-[#94969C]">Select multiple images</p>
              </label>

              {/* Folder Upload */}
              <label htmlFor="photo-upload-folder"
                className="border-2 border-dashed border-[#E2E4E7] rounded-xl p-5 text-center hover:border-[#4D11F6]/30 transition-colors cursor-pointer flex flex-col items-center justify-center gap-2">
                <input type="file" id="photo-upload-folder" className="hidden"
                  data-testid="photo-folder-input"
                  disabled={uploading || importingGdrive}
                  {...{ webkitdirectory: "", directory: "", mozdirectory: "" }}
                  onChange={(e) => { handleFileUpload(e.target.files); e.target.value = ''; }} />
                <ImageIcon size={24} className="text-[#94969C]" />
                <p className="text-sm font-medium text-[#1A2238]">Upload Folder</p>
                <p className="text-xs text-[#94969C]">Select an entire folder</p>
              </label>
            </div>

            {/* Google Drive Import */}
            <div className="bg-[#F3F3F9] rounded-xl p-4">
              <p className="text-sm font-medium text-[#1A2238] mb-2">Import from Google Drive</p>
              <p className="text-xs text-[#94969C] mb-3">Paste a public Google Drive folder link to import all images</p>
              <div className="flex gap-2">
                <Input
                  value={gdriveUrl}
                  onChange={(e) => setGdriveUrl(e.target.value)}
                  placeholder="https://drive.google.com/drive/folders/..."
                  data-testid="gdrive-url-input"
                  className="h-10 bg-white border-[#E2E4E7] flex-1 text-sm"
                  disabled={importingGdrive || uploading}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleGdriveImport())}
                />
                <Button onClick={handleGdriveImport}
                  data-testid="gdrive-import-btn"
                  disabled={!gdriveUrl.trim() || importingGdrive || uploading}
                  className="bg-[#4D11F6] hover:bg-[#3A0BC0] text-white h-10 px-4">
                  {importingGdrive ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Plus size={16} className="mr-1" />}
                  {importingGdrive ? 'Importing...' : 'Import'}
                </Button>
              </div>
              {importingGdrive && uploadProgress.total > 0 && (
                <div className="mt-3 space-y-2">
                  <div className="flex justify-between text-xs text-[#676970]">
                    <span>Downloading & optimizing...</span>
                    <span>{uploadProgress.current}/{uploadProgress.total} photos</span>
                  </div>
                  <div className="w-full bg-[#E2E4E7] rounded-full h-2 overflow-hidden">
                    <div className="bg-[#4D11F6] h-full rounded-full transition-all duration-500"
                      style={{ width: `${Math.round((uploadProgress.current / uploadProgress.total) * 100)}%` }} />
                  </div>
                </div>
              )}
            </div>

            <p className="text-xs text-[#94969C]">{photos.length}/50 photos added</p>

            {/* Photo Grid */}
            {photos.length > 0 && (
              <div className="grid grid-cols-4 gap-3">
                {photos.map((photo, idx) => {
                  const photoUrl = typeof photo === 'string' ? photo : (photo?.thumbnail_url || photo?.url || '');
                  return (
                  <div key={idx} className="relative group rounded-lg overflow-hidden border border-[#E2E4E7] aspect-video bg-[#F3F3F9]">
                    <img src={photoUrl} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
                    <button onClick={() => removePhoto(idx)}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <X size={12} />
                    </button>
                    <span className="absolute bottom-1 left-1 text-xs bg-black/60 text-white px-1.5 py-0.5 rounded">{idx + 1}</span>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {step === 5 && (
          <div className="space-y-6 text-center py-4" data-testid="step-generate-content">
            <div className="w-16 h-16 rounded-2xl bg-[#4D11F6]/10 flex items-center justify-center mx-auto mb-2">
              <Sparkles size={28} className="text-[#4D11F6]" />
            </div>
            <h3 className="text-xl font-semibold text-[#020B23]" style={{ fontFamily: 'Sora, sans-serif' }}>Generate Storyboard</h3>
            <p className="text-sm text-[#676970] max-w-md mx-auto">
              AI will analyze your property details and photos to create a complete walkthrough video storyboard.
            </p>
            <div className="bg-[#F3F3F9] rounded-xl p-5 max-w-md mx-auto text-left space-y-2">
              <p className="text-sm text-[#1A2238]"><strong>Property:</strong> {label}</p>
              <p className="text-sm text-[#1A2238]"><strong>Photos:</strong> {photos.length}</p>
              <p className="text-sm text-[#1A2238]"><strong>Agent:</strong> {agents.find(a => a.id === agentId)?.name || "Not selected"}</p>
              <p className="text-sm text-[#1A2238]"><strong>Voice:</strong> {voices.find(v => v.id === voiceId)?.name || "Not selected"}</p>
              <p className="text-sm text-[#1A2238]"><strong>Brand:</strong> {brands.find(b => b.id === brandId)?.name || "Not selected"}</p>
            </div>
            <Button onClick={handleGenerate} disabled={generating || !desc.trim()}
              data-testid="generate-storyboard-btn"
              className="bg-[#4D11F6] hover:bg-[#3A0BC0] text-white h-12 px-8 text-base font-medium">
              {generating ? (
                <><Loader2 size={18} className="mr-2 animate-spin" /> Generating Storyboard...</>
              ) : (
                <><Sparkles size={18} className="mr-2" /> Generate Storyboard</>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-6">
        <Button variant="outline" onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0}
          className="border-[#E2E4E7] text-[#676970]" data-testid="stepper-back-btn">
          <ChevronLeft size={16} className="mr-1" /> Back
        </Button>
        {step < 5 && (
          <Button onClick={handleNext} disabled={saving}
            data-testid="stepper-next-btn"
            className="bg-[#4D11F6] hover:bg-[#3A0BC0] text-white">
            {saving ? "Saving..." : "Next"} <ChevronRight size={16} className="ml-1" />
          </Button>
        )}
      </div>
    </div>
    </div>
  );
}
